import React, { useState } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { InputText } from "../../InputText";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { User, X, Loader2, KeyRound, Copy, Check } from "lucide-react";
import { api } from "../../../services/api";
import { Form, Formik, Field } from "formik";

interface PainelPracaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  nick: Yup.string().required("Nick é obrigatório"),
});

export function PainelPracaModal({
  isOpen,
  onClose,
  onSuccess
}: PainelPracaModalProps) {
  const [loading, setLoading] = useState(false);
  const [novaSenha, setNovaSenha] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (values: any, { resetForm }: any) => {
    setLoading(true);
    try {
      const response = await api.post("/militaresAcessos/praca", values);
      resetForm();
      setCopied(false);
      setNovaSenha(response.data.senha);
    } catch (error: any) {
      const message = error.response?.data?.message || "Erro ao criar acesso";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCopySenha = () => {
    if (!novaSenha) return;
    navigator.clipboard.writeText(novaSenha);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    if (novaSenha) onSuccess?.();
    setNovaSenha(null);
    setCopied(false);
    onClose();
  };

  const initialValues = {
    nick: "",
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-[oklch(0.45_0.15_142)]" />
              Criar Painel Praça
            </CardTitle>
            <CardDescription>
              Criar acesso de praça para um militar
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="pt-6">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form className="space-y-4">
                <div>
                  <Label htmlFor="nick">Nick</Label>
                  <Field
                    as={InputText}
                    id="nick"
                    name="nick"
                    title=""
                    placeholder="Digite o nick do militar"
                    className="mt-1"
                  />
                  {errors.nick && touched.nick && (
                    <p className="text-sm text-red-600 mt-1">{errors.nick as string}</p>
                  )}

                  {novaSenha && (
                    <div className="mt-3 p-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
                      <p className="text-xs font-medium text-green-700 dark:text-green-400 flex items-center gap-1 mb-2">
                        <KeyRound className="h-3.5 w-3.5" />
                        Acesso criado! Copie a senha gerada:
                      </p>
                      <button
                        type="button"
                        onClick={handleCopySenha}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-green-300 dark:border-green-700 bg-white dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-green-900 transition-colors cursor-pointer group"
                      >
                        <code className="text-base font-mono font-semibold tracking-widest text-foreground">
                          {novaSenha}
                        </code>
                        <span className="flex items-center gap-1 text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                          {copied ? (
                            <>
                              <Check className="h-4 w-4 text-green-500" />
                              <span className="text-green-500">Copiado!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              <span>Copiar</span>
                            </>
                          )}
                        </span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    {novaSenha ? "Fechar" : "Cancelar"}
                  </Button>
                  {!novaSenha && (
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        "Criar Acesso"
                      )}
                    </Button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </div>
  );
}
